import { addAssetRecoveryBreadcrumb } from './asset-recovery';

import { registerSW } from 'virtual:pwa-register';
import { Capacitor } from '@capacitor/core';

// Bump this marker to force a fresh asset hash and exercise the SW/asset
// update flow on the preview deployment.
const SW_BUILD_TAG = 'build-2026-07-06-2';

let hasBootstrappedServiceWorker = false;
let hasRegisteredUpdateListeners = false;
let serviceWorkerRegistration: ServiceWorkerRegistration | null = null;

const SERVICE_WORKER_UPDATE_INTERVAL_MS = 60 * 60 * 1000;

function isWebServiceWorkerContext() {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    !Capacitor.isNativePlatform()
  );
}

async function cleanupOldServiceWorkers() {
  try {
    const registrations = await navigator.serviceWorker.getRegistrations();

    for (const registration of registrations) {
      const scriptURL =
        registration.active?.scriptURL ||
        registration.installing?.scriptURL ||
        registration.waiting?.scriptURL;

      if (scriptURL?.includes('dev-sw.js')) {
        await registration.unregister();
      }
    }
  } catch (error) {
    console.error('[SW] Error cleaning up service workers:', error);
  }
}

function registerUpdateListeners() {
  if (hasRegisteredUpdateListeners) {
    return;
  }

  hasRegisteredUpdateListeners = true;

  // The service worker is now only a runtime-caching accelerator: it never
  // takes over open clients (skipWaiting/clientsClaim are off) and is no longer
  // responsible for avoiding the new-release crash — durable assets on R2 and a
  // fresh index.html handle that. So a failed update check is a non-event: we
  // refresh the registration opportunistically and never force a reload.
  const refreshRegistration = async (reason: string) => {
    if (!navigator.onLine || !serviceWorkerRegistration) {
      return;
    }

    try {
      await serviceWorkerRegistration.update();
    } catch (error) {
      addAssetRecoveryBreadcrumb(
        'service worker update check failed',
        {
          reason,
          errorMessage: error instanceof Error ? error.message : String(error),
        },
        'warning',
      );
    }
  };

  window.setInterval(() => {
    void refreshRegistration('interval');
  }, SERVICE_WORKER_UPDATE_INTERVAL_MS);
  window.addEventListener('online', () => {
    void refreshRegistration('online');
  });
}

export function bootstrapServiceWorker() {
  if (!isWebServiceWorkerContext() || hasBootstrappedServiceWorker) {
    return;
  }

  hasBootstrappedServiceWorker = true;

  void cleanupOldServiceWorkers();

  registerSW({
    immediate: true,
    onRegisteredSW(swUrl, registration) {
      console.log('[SW] Service Worker registered:', swUrl, SW_BUILD_TAG);
      addAssetRecoveryBreadcrumb('service worker registered', { swUrl });
      serviceWorkerRegistration = registration || null;
      registerUpdateListeners();
    },
    onRegisterError(error) {
      console.error('[SW] Service Worker registration error:', error);
      addAssetRecoveryBreadcrumb(
        'service worker registration error',
        {
          errorMessage: error instanceof Error ? error.message : String(error),
        },
        'error',
      );
    },
  });
}
